import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import AdminLayout from '../components/AdminLayout'
import RevenueSummary from './components/RevenueSummary'
import RevenueChart from './components/RevenueChart'
import PaymentMethodChart from './components/PaymentMethodChart'
import RecentPayments from './components/RecentPayments'
import LoadingSpinner from '../../../components/LoadingSpinner'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function AdminReports() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      const { data: subscriptions } = await supabase
        .from('subscriptions').select('*, supporters(full_name, membership_id, chapters(name))')
        .eq('club_id', CLUB_ID).order('payment_date', { ascending: false })
      const { data: settingsData } = await supabase.from('settings').select('*')
      const settings = {}
      settingsData?.forEach(s => settings[s.key] = s.value)
      const { count: totalMembers } = await supabase
        .from('supporters').select('*', { count: 'exact', head: true })
        .eq('club_id', CLUB_ID).eq('role', 'supporter')
      setData({ subscriptions: subscriptions || [], settings, totalMembers: totalMembers || 0 })
      setLoading(false)
    }
    fetchData()
  }, [])

  const handleExportCSV = () => {
    if (!data?.subscriptions?.length) return
    const currency = data.settings?.currency || 'ZMW'
    const rows = [
      ['Member ID', 'Full Name', 'Chapter', 'Amount', `Currency`, 'Payment Method', 'Payment Date', 'Valid Until', 'Transaction Ref'],
      ...data.subscriptions.map(s => [
        s.supporters?.membership_id || '',
        s.supporters?.full_name || '',
        s.supporters?.chapters?.name || '',
        s.amount,
        currency,
        s.payment_method || '',
        s.payment_date || '',
        s.valid_until || '',
        s.transaction_ref || '',
      ]),
    ]
    const csv = rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `mwfc-financial-report-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return <LoadingSpinner fullScreen />

  return (
    <AdminLayout>
      <div className="mb-6 lg:mb-8 flex justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
            Financial Reports
          </h1>
          <p className="text-gray-500 text-sm">Subscription revenue and payment analytics</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={!data?.subscriptions?.length}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-200 dark:disabled:bg-white/5 disabled:text-gray-400 text-white px-4 sm:px-6 py-3 font-bold uppercase tracking-widest text-xs sm:text-sm transition-colors whitespace-nowrap"
        >
          Export CSV
        </button>
      </div>
      <RevenueSummary
        subscriptions={data.subscriptions}
        settings={data.settings}
        totalMembers={data.totalMembers}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-4 lg:mt-6">
        <RevenueChart subscriptions={data.subscriptions} settings={data.settings} />
        <PaymentMethodChart subscriptions={data.subscriptions} />
      </div>
      <div className="mt-4 lg:mt-6">
        <RecentPayments subscriptions={data.subscriptions} settings={data.settings} />
      </div>
    </AdminLayout>
  )
}